import {
	CampaignDisplayStatus,
	CampaignRoutingSettings,
	CampaignRoutingTargetState,
} from "@signalist/shared-types";

import { ROUTING_I18N } from "./campaignRoutingFields";

/** One campaign of the list as the routing screens show it. */
export interface RoutingCampaign {
	uuid: string;
	name: string;
	isDefault: boolean;
	state: CampaignRoutingTargetState;
}

/** The slice of `list.campaignConfig` routing reads, keyed by campaign uuid (plus the "name" key). */
export type RoutingCampaignConfig = Record<string, unknown> | null | undefined;

export type RoutingCampaignStatuses =
	| Record<string, { status?: CampaignDisplayStatus } | undefined>
	| null
	| undefined;

/**
 * A campaign's routing target state from what the campaign table shows (its display status), for a
 * campaign the server has not judged in this answer. Mirrors the API's `campaignTargetStateOf`:
 * running → VALID, paused, ended, never started → NOT_ACTIVATED, and a campaign that cannot send
 * → INVALID.
 */
export const targetStateOfDisplay = (
	status: CampaignDisplayStatus | undefined,
	isValid: boolean
): CampaignRoutingTargetState => {
	if (status === undefined) {
		return isValid ? "NOT_ACTIVATED" : "INVALID";
	}

	switch (status) {
		case CampaignDisplayStatus.INVALID:
			return "INVALID";
		case CampaignDisplayStatus.ACTIVE:
		case CampaignDisplayStatus.LOADING:
			return "VALID";
		case CampaignDisplayStatus.PAUSED:
			return "PAUSED";
		case CampaignDisplayStatus.ENDED:
			return "ENDED";
		case CampaignDisplayStatus.VALID:
		case CampaignDisplayStatus.READY:
			return "NOT_ACTIVATED";
		default: {
			const unknownStatus: never = status;

			throw new Error(`Unknown campaign display status ${String(unknownStatus)}`);
		}
	}
};

const isCampaignEntry = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * The list's campaigns in config order, each with its routing target state. Where the server
 * judged a campaign in its routing answer (a rule's target, the default), its state wins: it also
 * knows about deleted campaigns and empty message steps.
 */
export const routingCampaignsOf = (
	config: RoutingCampaignConfig,
	statuses: RoutingCampaignStatuses,
	settings?: CampaignRoutingSettings
): RoutingCampaign[] => {
	const serverStates = new Map<string, CampaignRoutingTargetState>();

	settings?.rules.forEach((rule) => serverStates.set(rule.campaignUuid, rule.targetState));

	if (settings?.defaultCampaignUuid && settings.defaultCampaignState) {
		serverStates.set(settings.defaultCampaignUuid, settings.defaultCampaignState);
	}

	return Object.entries(config ?? {})
		.filter(([key, value]) => key !== "name" && isCampaignEntry(value))
		.map(([uuid, value]) => {
			const entry = value as Record<string, unknown>;

			return {
				uuid,
				name: typeof entry.name === "string" && entry.name ? entry.name : uuid,
				isDefault: entry.isDefault === true,
				state:
					serverStates.get(uuid) ??
					targetStateOfDisplay(statuses?.[uuid]?.status, entry.isValid === true),
			};
		});
};

/** A rule target's state: a campaign no longer in the list is DELETED. */
export const targetStateOf = (
	uuid: string,
	campaigns: readonly RoutingCampaign[]
): CampaignRoutingTargetState =>
	campaigns.find((campaign) => campaign.uuid === uuid)?.state ?? "DELETED";

/** How a target state is drawn: its pill's label key and colours. */
export const targetPillOf = (
	state: CampaignRoutingTargetState
): { labelKey: string; className: string } => {
	const labelKey = `${ROUTING_I18N}.targetStates.${state}`;

	switch (state) {
		case "VALID":
			return { labelKey, className: "bg-status-success-light text-status-success" };
		case "NOT_ACTIVATED":
			return { labelKey, className: "bg-status-running-light text-status-running" };
		case "PAUSED":
		case "ENDED":
			return { labelKey, className: "bg-status-paused-light text-status-paused" };
		case "INVALID":
		case "DELETED":
			return { labelKey, className: "bg-status-error-light text-status-error" };
		default: {
			const unknownState: never = state;

			throw new Error(`Unknown routing target state ${String(unknownState)}`);
		}
	}
};

/** The note under a rule's campaign picker, or none for a running campaign. */
export const targetNoteKeyOf = (state: CampaignRoutingTargetState): string | null => {
	switch (state) {
		case "VALID":
			return null;
		case "NOT_ACTIVATED":
			return `${ROUTING_I18N}.editor.waitNote`;
		case "PAUSED":
			return `${ROUTING_I18N}.editor.pausedNote`;
		case "ENDED":
			return `${ROUTING_I18N}.editor.endedNote`;
		case "INVALID":
			return `${ROUTING_I18N}.editor.skippedNote`;
		case "DELETED":
			return `${ROUTING_I18N}.editor.deletedNote`;
		default: {
			const unknownState: never = state;

			throw new Error(`Unknown routing target state ${String(unknownState)}`);
		}
	}
};
